import { FitScoreSummaryResponse } from '@/data/usecases/content/remote-user-content-summary'
import { i18nTranslationHelper } from '@/i18n'
import {
  FitScoreSkillCompatibility,
  FitScoreSkillKey,
  FitScoreSkillsCompatibility,
  FitScoreSummary,
  FormattedFitScore,
  FormattedFitScoreSkill,
  ProductFitScoreResponse
} from './product-fit-score-service-types'

export const FitScoreLevel = {
  HIGH: 'HIGH',
  MEDIUM: 'MEDIUM',
  LOW: 'LOW'
} as const

export type FitScoreLevel = (typeof FitScoreLevel)[keyof typeof FitScoreLevel]

export const FIT_SCORE_SKILL_KEYS: FitScoreSkillKey[] = [
  'professionalRole',
  'seniority',
  'language'
]

// Query param usado pra propagar o productId entre a página de recomendações de fit-score
// e as telas de edição de perfil (/user/profile, /user/interest-areas-skills). Quando presente,
// as telas de edição refazem o fetch do fit-score após salvar e redirecionam o user conforme
// o novo effectiveLevel.
export const FIT_SCORE_PRODUCT_ID_PARAM = 'fitScoreProductId'

// CSS vars mapeadas por nível — usadas direto em `sx={{ color: ... }}` sem precisar de theme
const LEVEL_COLOR_VAR: Record<FitScoreLevel, string> = {
  HIGH: 'var(--success-500)',
  MEDIUM: 'var(--warning-500)',
  LOW: 'var(--error-500)'
}

const filterIncompatibleSkills = (skills: FitScoreSkillsCompatibility): FormattedFitScoreSkill[] =>
  FIT_SCORE_SKILL_KEYS.flatMap(key => {
    const data = skills[key] as FitScoreSkillCompatibility | undefined
    if (!data || data.compatibility === FitScoreLevel.HIGH) return []
    return [{ key, data }]
  })

/**
 * Formato compact `{ level, approved }` usado em listas (search, recommended, summary).
 * Carregar o `skillsCompatibility` completo em todos os itens de lista seria caro;
 * esse helper centraliza a transformação que estava duplicada em 4 lugares.
 *
 * O input é estrutural pra acomodar diferentes shapes de resposta — listas
 * costumam tipar `profileCompatibility` como `string` cru, enquanto o detalhe
 * já tipa como `FitScoreLevel`. Faz cast pra `FitScoreLevel` na saída.
 */
export const formatFitScoreSummary = (
  raw: FitScoreSummaryResponse | null | undefined
): FitScoreSummary | null => {
  if (!raw) return null

  return {
    level: raw.profileCompatibility as FitScoreLevel,
    approved: raw.approved ?? null
  }
}

/**
 * Título do bloco fit-score — depende de namespace diferente conforme `approved`
 * (components.fitScoreDetail.approved.label vs enums.fitScore.<level>.label).
 */
export const resolveFitScoreTitle = (fitScore: FormattedFitScore): string =>
  fitScore.isApproved
    ? i18nTranslationHelper.getTComponents('fitScoreDetail.approved.label')
    : i18nTranslationHelper.getTEnums(`fitScore.${fitScore.profileCompatibility}.label`)

/**
 * Formata a resposta crua do endpoint /catalog/v2/products/{id}/fit-score em uma
 * estrutura pronta para consumo pelos componentes — chamada no data loader para que
 * o dado já chegue mastigado, sem lógica de transformação em tempo de render.
 */
export const formatProductFitScore = (data: ProductFitScoreResponse): FormattedFitScore => {
  const raw = data.fitScore
  const approved = raw.approved ?? null
  const isApproved = approved === true
  // Aprovação manual do CX promove o perfil pra HIGH em toda a UI (cor, ícone, copy).
  // Derivamos uma única vez no formatter pra que consumers não repliquem essa regra.
  const effectiveLevel = approved ? FitScoreLevel.HIGH : raw.profileCompatibility
  const isHigh = effectiveLevel === FitScoreLevel.HIGH
  const showRecommendations =
    effectiveLevel === FitScoreLevel.LOW || effectiveLevel === FitScoreLevel.MEDIUM
  const descriptionI18nKey = isApproved
    ? 'fitScoreDetail.approved.description'
    : `fitScoreDetail.${raw.profileCompatibility}.description`

  return {
    profileCompatibility: raw.profileCompatibility,
    skillsCompatibility: raw.skillsCompatibility,
    effectiveLevel,
    levelColor: LEVEL_COLOR_VAR[effectiveLevel],
    incompatibleSkills: filterIncompatibleSkills(raw.skillsCompatibility),
    approved,
    isApproved,
    isHigh,
    showRecommendations,
    descriptionI18nKey
  }
}

// Mapeia o código ISO de idioma (vindo do backend) para:
// - enumKey: chave do enum proficientLanguage no i18n (system.entities.enums.proficientLanguage.<KEY>.label)
// - searchQuery: keyword PT usado na busca textual de cursos. A API /v2/user/content/search
//   tem o param `languages` mas o filtro retorna vazio para códigos como "en", então usamos
//   busca por texto + categoria LANGUAGE como fallback.
interface LanguageMapping {
  enumKey: string
  searchQuery: string
}

const LANGUAGE_MAP: Record<string, LanguageMapping> = {
  pt: { enumKey: 'PORTUGUESE', searchQuery: 'Português' },
  en: { enumKey: 'ENGLISH', searchQuery: 'Inglês' },
  es: { enumKey: 'SPANISH', searchQuery: 'Espanhol' },
  zh: { enumKey: 'MANDARIN', searchQuery: 'Mandarim' },
  ar: { enumKey: 'ARABIC', searchQuery: 'Árabe' },
  ja: { enumKey: 'JAPANESE', searchQuery: 'Japonês' },
  de: { enumKey: 'GERMAN', searchQuery: 'Alemão' }
}

const resolveLanguage = (code: string | undefined | null): LanguageMapping | null => {
  if (!code) return null
  return LANGUAGE_MAP[code.toLowerCase()] ?? null
}

export const getProficientLanguageEnumKey = (code: string | undefined | null): string | null =>
  resolveLanguage(code)?.enumKey ?? null

export const getLanguageSearchQuery = (code: string | undefined | null): string | null =>
  resolveLanguage(code)?.searchQuery ?? null
